import React from "react";
import { Users, Layout, Target, HandsClapping, Medal, Rocket } from "@phosphor-icons/react";
import { defaultLayout } from "pages/layout/default";
import SEO from "lib/components/SEO";
import { breadcrumbLd, organizationLd, localBusinessLd, faqLd, mentorTeamLd } from "lib/seo/jsonLd";
import { motion } from "framer-motion";
import {
  fadeUp,
  fadeLeft,
  fadeRight,
  staggerContainer,
  cardItem,
} from "lib/animations";
import { useInView } from "lib/hooks/useInView";

const data = {
  missions: [
    {
      icon: <Users className="w-6 h-6 text-primary" />,
      heading: "Reach 2 Lakh Students Across Nepal",
      para: "We've already helped over 100,000 students prepare for the IOE entrance. Our goal is to reach 2 lakh students — in Kathmandu and every district beyond — with the same structured coaching.",
    },
    {
      icon: <Layout className="w-7 h-7 text-primary" />,
      heading: "Teach Exactly What the Exam Tests",
      para: "No off-syllabus filler. Live classes, structured notes, and mock tests built around the real IOE and CSIT exam pattern, so every hour you study moves your rank.",
    },
    {
      icon: <Target className="w-6 h-6 text-primary" />,
      heading: "Keep Entrance Prep Affordable",
      para: "Good coaching shouldn't be reserved for students who can pay premium fees. Our courses, mock tests, and study materials are priced so any serious student in Nepal can access them.",
    },
  ],
  values: [
    {
      title: "Build Nepal's Leading Learning Community for Entrance Preparation",
      theme: "bg-primary",
      icon: <HandsClapping className="w-16 h-16 text-white" weight="fill" />,
    },
    {
      title: "Transform Students' Dreams into Successful IOE Entrance Results",
      theme: "bg-primary-700",
      icon: <Medal className="w-16 h-16 text-white" weight="fill" />,
    },
    {
      title: "Revolutionize Digital Education in Nepal",
      theme: "bg-gray-800",
      icon: <Rocket className="w-16 h-16 text-white" weight="fill" />,
    },
  ],
};

function AboutUs() {
  const [valuesRef, valuesInView] = useInView<HTMLDivElement>();
  const [missionRef, missionInView] = useInView<HTMLDivElement>();
  const [empathyRef, empathyInView] = useInView<HTMLDivElement>();
  const [ctaRef, ctaInView] = useInView<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Clamphook Academy | Nepal's #1 IOE & CSIT Entrance Preparation Institute"
        description="Clamphook Academy is Nepal's leading IOE & BSc CSIT entrance preparation institute — founded in 2078 BS by Pulchowk engineering graduates, trusted by 100,000+ students across Nepal. Online and physical classes, weekly CBT mock tests, and expert mentors."
        canonicalUrl="https://clamphook.com/aboutus"
        ogType="website"
        ogImage="/img/logo/App_Icon_Primary.png"
        keywords={[
          "about Clamphook Academy",
          "best IOE entrance preparation Nepal",
          "CSIT entrance institute Kathmandu",
          "online IOE coaching Nepal",
          "Clamphook founders",
          "Pulchowk engineering entrance",
          "IOE preparation institute founded by Pulchowk alumni",
          "best engineering coaching Nepal",
        ]}
        jsonLd={[
          organizationLd(),
          breadcrumbLd([
            { name: "Home", url: "/" },
            { name: "About Us", url: "/aboutus" },
          ]),
          localBusinessLd(),
          faqLd([
            {
              question: "Who founded Clamphook Academy?",
              answer:
                "Clamphook Academy was founded in 2078 BS (2021 AD) by a group of Pulchowk Campus (IOE Central Campus) engineering graduates. Every core mentor has personally cleared the IOE entrance exam and studied at top TU engineering campuses.",
            },
            {
              question: "Where is Clamphook Academy located?",
              answer:
                "Clamphook Academy's physical classes run in Kathmandu, Nepal. Online courses are accessible from anywhere in Nepal — Pokhara, Birgunj, Itahari, Dharan, Butwal, and beyond.",
            },
            {
              question: "What exams does Clamphook prepare students for?",
              answer:
                "Clamphook prepares students for the IOE (Institute of Engineering) entrance exam for BE/B.Arch programs at Pulchowk, Thapathali, and other TU engineering campuses, and the TU BSc CSIT entrance exam. It also offers NEB-to-IOE bridging courses.",
            },
            {
              question: "How many students has Clamphook helped?",
              answer:
                "Over 100,000 students across Nepal have enrolled in Clamphook's IOE and CSIT entrance preparation courses since its founding.",
            },
          ]),
          ...mentorTeamLd(),
        ]}
      />

      {/* Values Section */}
      <section
        ref={valuesRef}
        className="container max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20"
      >
        <motion.h2
          className="text-sm font-semibold text-primary text-center tracking-wider uppercase mb-4"
          variants={fadeUp}
          initial="hidden"
          animate={valuesInView ? "show" : "hidden"}
        >
          Values
        </motion.h2>
        <motion.h3
          className="text-3xl font-bold text-gray-800 text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          animate={valuesInView ? "show" : "hidden"}
          transition={{ delay: 0.1 }}
        >
          Our Core Vision
        </motion.h3>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="hidden"
          animate={valuesInView ? "show" : "hidden"}
        >
          {data.values.map((value, index) => (
            <motion.div
              key={index}
              variants={cardItem}
              whileHover={{ scale: 1.05, y: -6 }}
              className={`flex flex-col items-center justify-center px-8 py-16 rounded-3xl ${value.theme} transition-shadow duration-300 cursor-default`}
            >
              <motion.div
                className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mb-8"
                initial={{ scale: 0, rotate: -20 }}
                animate={valuesInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -20 }}
                transition={{ delay: 0.2 + index * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {value.icon}
              </motion.div>
              <h4 className="text-lg font-bold text-center text-white leading-snug px-4">
                {value.title}
              </h4>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Mission Section */}
      <section
        ref={missionRef}
        className="container max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-20"
      >
        <motion.p
          className="text-sm font-semibold text-primary text-center tracking-wider uppercase mb-4"
          variants={fadeUp}
          initial="hidden"
          animate={missionInView ? "show" : "hidden"}
        >
          Our Mission
        </motion.p>
        <motion.h1
          className="text-3xl font-bold text-gray-800 text-center mb-4"
          variants={fadeUp}
          initial="hidden"
          animate={missionInView ? "show" : "hidden"}
          transition={{ delay: 0.08 }}
        >
          Transforming IOE Entrance Preparation in Nepal
        </motion.h1>
        <motion.p
          className="text-center text-gray-600 mb-16 max-w-2xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          animate={missionInView ? "show" : "hidden"}
          transition={{ delay: 0.16 }}
        >
          Founded in 2078 BS by a group of Pulchowk engineering graduates,
          Clamphook has grown into Nepal&apos;s most trusted IOE entrance
          preparation platform — interactive classes, weekly mock tests, and
          complete study materials for students in Kathmandu and across the
          country.
        </motion.p>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12"
          variants={staggerContainer}
          initial="hidden"
          animate={missionInView ? "show" : "hidden"}
        >
          {data.missions.map((mission, index) => (
            <motion.div
              key={index}
              variants={cardItem}
              whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
              className="flex flex-col items-start p-6 bg-white rounded-2xl border border-gray-100 shadow-sm transition-shadow duration-300"
            >
              <motion.div
                className="rounded-xl h-14 w-14 bg-primary-50 flex items-center justify-center mb-6"
                whileHover={{ rotate: 8, scale: 1.1 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {mission.icon}
              </motion.div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3 leading-snug">
                {mission.heading}
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                {mission.para}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Empathy Section */}
      <div ref={empathyRef} className="bg-primary-50">
        <section className="container max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <motion.h2
            className="text-2xl sm:text-3xl text-gray-800 font-bold text-center mb-8"
            variants={fadeUp}
            initial="hidden"
            animate={empathyInView ? "show" : "hidden"}
          >
            We&apos;ve been in your seat
          </motion.h2>
          <motion.div
            className="space-y-6 text-gray-700 leading-relaxed"
            variants={staggerContainer}
            initial="hidden"
            animate={empathyInView ? "show" : "hidden"}
          >
            <motion.p variants={fadeUp}>
              Every mentor at Clamphook sat the same entrance exam you&apos;re
              preparing for. We remember the pressure of a single test
              deciding which college you get into, and how hard it is to
              find study material you can actually trust. That&apos;s why we
              built the course we wish we&apos;d had.
            </motion.p>
            <motion.p variants={fadeUp}>
              We take your effort seriously — the structured notes, the
              weekly mock tests, and the same-day doubt support all exist for
              one reason: to give you the most direct path to your rank.
            </motion.p>
          </motion.div>
        </section>
      </div>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="container max-w-6xl mx-auto px-4 sm:px-6 py-16 mb-16"
      >
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={staggerContainer}
          initial="hidden"
          animate={ctaInView ? "show" : "hidden"}
        >
          <motion.div
            variants={fadeLeft}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-primary rounded-3xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-3 text-white">Forums</h3>
            <p className="mb-6 text-primary-100">
              A great place to make new friends and find information regarding
              your studies and enquiries
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="bg-white text-primary px-6 py-2 rounded-full font-semibold hover:bg-primary-50 transition-colors"
            >
              Coming Soon
            </motion.button>
          </motion.div>

          <motion.div
            variants={fadeRight}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gray-800 rounded-3xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold text-white mb-3">FAQs</h3>
            <p className="mb-6 text-gray-300">
              Find the most asked questions on the platform. Uga and Buga are
              there to help you
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => { window.location.href = "/faqs"; }}
              className="bg-white text-gray-800 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-colors"
            >
              Learn More
            </motion.button>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}

// @ts-ignore
AboutUs.getLayout = defaultLayout;

export default AboutUs;
